import {
  AlfredPricingCatalog,
  Route,
  UpsertAlfredPricingModule,
  UpsertAlfredPricingPackage,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

async function fetchAlfredPricingCatalog(): Promise<AlfredPricingCatalog> {
  return await request<AlfredPricingCatalog>({
    url: Route.ALFRED__PRICING,
    method: "GET",
  })
}

/** Resolves to the public URL of an image for a module text. */
export async function uploadAlfredPricingImage(file: File): Promise<string> {
  const formData = new FormData()
  formData.append("image", file)
  const { imageUrl } = await request<{ imageUrl: string }>({
    url: Route.ALFRED__ADMIN_PRICING_IMAGES,
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
  return imageUrl
}

const alfredPricingQueries = {
  all: () => ["alfred-pricing"],
  catalog: () =>
    queryOptions({
      queryKey: [...alfredPricingQueries.all(), "catalog"],
      queryFn: () => fetchAlfredPricingCatalog(),
    }),
}

export function useAlfredPricingMutations() {
  const queryClient = useQueryClient()
  const handleSettled = () =>
    queryClient.invalidateQueries({ queryKey: alfredPricingQueries.all() })

  const createModuleMutation = useMutation({
    mutationFn: (data: UpsertAlfredPricingModule) =>
      request<AlfredPricingCatalog>({
        url: Route.ALFRED__ADMIN_PRICING_MODULES,
        method: "POST",
        data,
      }),
    onSettled: handleSettled,
  })

  const updateModuleMutation = useMutation({
    mutationFn: ({
      moduleId,
      data,
    }: {
      moduleId: string
      data: UpsertAlfredPricingModule
    }) =>
      request<AlfredPricingCatalog>({
        url: populateRoute(Route.ALFRED__ADMIN_PRICING_MODULE, moduleId),
        method: "PATCH",
        data,
      }),
    onSettled: handleSettled,
  })

  const deleteModuleMutation = useMutation({
    mutationFn: (moduleId: string) =>
      request<AlfredPricingCatalog>({
        url: populateRoute(Route.ALFRED__ADMIN_PRICING_MODULE, moduleId),
        method: "DELETE",
      }),
    onSettled: handleSettled,
  })

  const createPackageMutation = useMutation({
    mutationFn: (data: UpsertAlfredPricingPackage) =>
      request<AlfredPricingCatalog>({
        url: Route.ALFRED__ADMIN_PRICING_PACKAGES,
        method: "POST",
        data,
      }),
    onSettled: handleSettled,
  })

  const updatePackageMutation = useMutation({
    mutationFn: ({
      packageId,
      data,
    }: {
      packageId: string
      data: UpsertAlfredPricingPackage
    }) =>
      request<AlfredPricingCatalog>({
        url: populateRoute(Route.ALFRED__ADMIN_PRICING_PACKAGE, packageId),
        method: "PATCH",
        data,
      }),
    onSettled: handleSettled,
  })

  const deletePackageMutation = useMutation({
    mutationFn: (packageId: string) =>
      request<AlfredPricingCatalog>({
        url: populateRoute(Route.ALFRED__ADMIN_PRICING_PACKAGE, packageId),
        method: "DELETE",
      }),
    onSettled: handleSettled,
  })

  return {
    createModuleMutation,
    updateModuleMutation,
    deleteModuleMutation,
    createPackageMutation,
    updatePackageMutation,
    deletePackageMutation,
  }
}

export default alfredPricingQueries
